import { useEffect, useState } from 'react';
import {
  githubUserOrganizationList,
  GitHubOrganization,
} from '../../api/github/userOrganizationList';
import { githubRepositoryList } from '../../api/github/repositoryList';
import { toAppError } from '../../api/errors';
import { getGitHubUser } from '../../auth/github';
import { vscodeTheme } from '../../styles/theme';
import { SearchableSelect } from '../shared/SearchableSelect';
import { useToast } from '../shared/Toast';

interface GitHubRepositorySelection {
  owner: string;
  repo: string;
  branch: string;
}

interface TargetSelection {
  login: string;
  type: 'user' | 'org';
}

interface GitHubRepositorySelectorProps {
  selectedRepository: GitHubRepositorySelection | null;
  onRepositorySelect: (repository: GitHubRepositorySelection | null) => void;
  onLogout: () => void;
}

export function GitHubRepositorySelector({
  selectedRepository,
  onRepositorySelect,
  onLogout,
}: GitHubRepositorySelectorProps) {
  const STORAGE_KEY = 'github_selected_target';
  const { showToast } = useToast();
  const [repositories, setRepositories] = useState<
    Array<{
      owner: string;
      name: string;
      full_name: string;
      default_branch: string;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [target, setTarget] = useState<TargetSelection | null>(null);
  const [organizations, setOrganizations] = useState<GitHubOrganization[]>([]);
  const [userLogin, setUserLogin] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const loadTargetOptions = async () => {
      try {
        const [userResult, orgResult] = await Promise.allSettled([
          getGitHubUser(),
          githubUserOrganizationList({ per_page: 100 }),
        ]);

        if (userResult.status === 'rejected') {
          throw userResult.reason;
        }

        const user = userResult.value;
        const organizations =
          orgResult.status === 'fulfilled' ? orgResult.value.organizations : [];

        setUserLogin(user.login);
        setOrganizations(organizations);

        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          setTarget(JSON.parse(saved));
        } else if (organizations.length > 0) {
          const firstOrg = organizations[0];
          setTarget({ login: firstOrg.login, type: 'org' });
        } else {
          setTarget({ login: user.login, type: 'user' });
        }
      } catch (error) {
        const message = toAppError(error).message;
        setError(message);
        setLoading(false);
      }
    };

    void loadTargetOptions();
  }, []);

  useEffect(() => {
    if (!target) return;

    const loadRepositories = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await githubRepositoryList({
          target: target.login,
          owner: target.type,
          per_page: 100,
        });
        setRepositories(data.repositories);
        if (!selectedRepository && data.repositories.length > 0) {
          const first = data.repositories[0];
          onRepositorySelect({
            owner: first.owner,
            repo: first.name,
            branch: first.default_branch,
          });
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load repositories';
        setError(message);
        showToast('リポジトリの取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    };

    void loadRepositories();
  }, [onRepositorySelect, selectedRepository, showToast, target]);

  const handleTargetSelect = (newTarget: TargetSelection) => {
    setTarget(newTarget);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newTarget));
    setIsDropdownOpen(false);
    onRepositorySelect(null);
  };

  if (loading) {
    return (
      <div style={{ color: vscodeTheme.colors.textPrimary, fontSize: '13px' }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return <div style={{ color: '#f48771', fontSize: '13px' }}>Error: {error}</div>;
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '4px',
        }}
      >
        <label
          style={{
            color: vscodeTheme.colors.textSecondary,
            fontSize: '11px',
            fontFamily: vscodeTheme.typography.fontFamily,
            display: 'block',
          }}
        >
          REPOSITORY
        </label>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: vscodeTheme.colors.textSecondary,
              fontSize: '11px',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {target?.login ?? '...'} ▼
          </button>
          {isDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: vscodeTheme.colors.editorBackground,
                border: `1px solid ${vscodeTheme.colors.border}`,
                borderRadius: '2px',
                zIndex: 100,
                minWidth: '150px',
                marginTop: '4px',
              }}
            >
              {userLogin && (
                <div
                  onClick={() =>
                    handleTargetSelect({ login: userLogin, type: 'user' })
                  }
                  style={{
                    padding: '6px 8px',
                    cursor: 'pointer',
                    backgroundColor:
                      target?.login === userLogin
                        ? vscodeTheme.colors.activeBackground
                        : 'transparent',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '11px',
                  }}
                >
                  {userLogin} (個人)
                </div>
              )}
              {organizations.map((org) => (
                <div
                  key={org.id}
                  onClick={() =>
                    handleTargetSelect({ login: org.login, type: 'org' })
                  }
                  style={{
                    padding: '6px 8px',
                    cursor: 'pointer',
                    backgroundColor:
                      target?.login === org.login
                        ? vscodeTheme.colors.activeBackground
                        : 'transparent',
                    color: vscodeTheme.colors.textPrimary,
                    fontSize: '11px',
                  }}
                >
                  {org.login}
                </div>
              ))}
              <div
                style={{
                  borderTop: `1px solid ${vscodeTheme.colors.border}`,
                  margin: '4px 0',
                }}
              />
              <div
                onClick={onLogout}
                style={{
                  padding: '6px 8px',
                  cursor: 'pointer',
                  color: vscodeTheme.colors.textSecondary,
                  fontSize: '11px',
                }}
              >
                ログアウト
              </div>
            </div>
          )}
        </div>
      </div>
      <SearchableSelect
        label={undefined}
        items={repositories.map((repo) => ({
          id: repo.full_name,
          label: repo.full_name,
        }))}
        selectedId={
          selectedRepository
            ? `${selectedRepository.owner}/${selectedRepository.repo}`
            : null
        }
        onChange={(selectedId) => {
          const repo = repositories.find((item) => item.full_name === selectedId);
          if (!repo) {
            return;
          }
          onRepositorySelect({
            owner: repo.owner,
            repo: repo.name,
            branch: repo.default_branch,
          });
        }}
        placeholder={selectedRepository?.repo ?? 'Filter...'}
        filterText={filterText}
        onFilterTextChange={setFilterText}
        emptyText="No repositories found"
      />
    </div>
  );
}
